import {
  Alert,
  FormField,
  Grid,
  NonCancelableCustomEvent,
  Textarea,
  TextareaProps,
} from '@amzn/awsui-components-core';
import {
  Content,
  ContentAccessUser,
  ContentWorkflow,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { type JSX } from 'react';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import { getUserRoles } from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import AppContextProps from '../../../contexts/AppContextProps';
import '../../../resources/styles/scss/components/common/WorkflowControls.css';
import ContentReviewers from '../../content/ContentReviewers';

interface WorkflowControlsProps extends AppContextProps {
  contentId: string;
  content?: Content;
  workflow?: ContentWorkflow;
  disabled: boolean;
  validationError?: any;
  onActionSuccess?: () => void;
  tabView?: boolean;
  inEditMode?: boolean;
  onUpdateReviewers?: (reviwers: ContentAccessUser[]) => void;
  loading?: boolean;
  isEditReviewersDisabled?: boolean;
  isDeleteReviewersDisabled?: boolean;
  readView?: boolean;
  showCannotDeleteReviewerAlert?: boolean;
  isSubmitForReviewDisabled?: boolean;
  isContentUnchanged?: boolean;
  handleSaveChanges?: (unlock: boolean) => Promise<any>;
  handleSubmitForReview?: () => Promise<any>;
  validateSubmitForReview?: () => Promise<boolean>;
}

interface WorkflowControlsState {
  comment: string;
  validationError?: any;
  reviewers?: ContentAccessUser[];
}

abstract class WorkflowControls extends React.Component<
  WorkflowControlsProps,
  WorkflowControlsState
> {
  public static defaultProps = {
    disabled: false,
  };

  protected showCommentForm: boolean = false;
  protected canEditReviewers: boolean = false;

  protected readonly mplServiceClient: IMplServiceClient;

  constructor(props: WorkflowControlsProps) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.state = {
      comment: '',
    };
  }

  public render() {
    const {
      disabled,
      workflow,
      tabView,
      isEditReviewersDisabled,
      isDeleteReviewersDisabled,
      readView,
      showCannotDeleteReviewerAlert,
      isSubmitForReviewDisabled,
      isContentUnchanged,
      handleSaveChanges,
      handleSubmitForReview,
      validateSubmitForReview,
    } = this.props;
    const { validationError, reviewers } = this.state;

    const buttonsDisabled = disabled || !!this.hasError(validationError);
    const commentForm = this.showCommentForm ? (
      <Grid gridDefinition={[{ colspan: 12 }]}>
        <FormField
          errorText={validationError && validationError.comment}
          stretch
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Textarea
            value={this.state.comment}
            onChange={this.handleCommentChange}
            placeholder={constants.REVIEW_COMMENTS_PLACEHOLDER}
            rows={10}
            disabled={disabled}
            ariaRequired
          />
        </FormField>
      </Grid>
    ) : null;

    const submitForReviewInEditModeWarning =
      !!readView && this.isNewOrModified() ? (
        <Alert id="submit-in-edit-mode-alert" type="warning">
          {constants.SUBMIT_FOR_REVIEW_IN_EDIT_MODE_WARNING}
        </Alert>
      ) : null;

    const reviewerForm = (
      <>
        <FormField
          errorText={!tabView && !readView && validationError && validationError.reviewers}
          stretch
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <ContentReviewers
            // @ts-expect-error TS2322 - strict migration
            workflow={workflow}
            reviewers={reviewers}
            onReviewersUpdated={this.updateReviewers}
            readonly={!this.canEditReviewers}
            isEditReviewersDisabled={isEditReviewersDisabled}
            isDeleteReviewersDisabled={isDeleteReviewersDisabled}
            readView={!!readView}
            showCannotDeleteReviewerAlert={showCannotDeleteReviewerAlert}
            // @ts-expect-error TS2322 - strict migration
            isSubmitForReviewDisabled={isSubmitForReviewDisabled}
            // @ts-expect-error TS2322 - strict migration
            isContentUnchanged={isContentUnchanged}
            // @ts-expect-error TS2322 - strict migration
            handleSaveChanges={handleSaveChanges}
            // @ts-expect-error TS2322 - strict migration
            handleSubmitForReview={handleSubmitForReview}
            // @ts-expect-error TS2322 - strict migration
            validateSubmitForReview={validateSubmitForReview}
          />
        </FormField>
        {submitForReviewInEditModeWarning}
      </>
    );

    const actionControls =
      !tabView && !readView ? (
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <div className="awsui-cat-flex awsui-cat-flex-end">
            {this.renderActionControls(buttonsDisabled)}
          </div>
        </Grid>
      ) : null;

    const reviewerFormId = tabView ? 'reviewer-form-tab-view' : '';

    return (
      <div>
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <div id={reviewerFormId}>{reviewerForm}</div>
        </Grid>
        {commentForm}
        {actionControls}
      </div>
    );
  }

  protected abstract renderActionControls(disabled: boolean): JSX.Element | null | false;

  protected sendWorkflowEvent = async (eventType: WorkflowEventTypes, data?: object) => {
    const { contentId, workflow } = this.props;
    const { comment } = this.state;
    let workflowId = workflow?.draftWorkflowId;

    if (
      [
        WorkflowEventTypes.FINALIZE,
        WorkflowEventTypes.DISTRIBUTE,
        WorkflowEventTypes.REVOKE,
      ].includes(eventType)
    ) {
      workflowId = workflow?.publicationWorkflowId;
    }

    // @ts-expect-error TS2345 - strict migration
    await createWorkflowEventWithStatePolling(contentId, workflowId, eventType, comment, data);
  };

  protected validateInputs = async () => {
    const validationError = this.props.validationError || {};
    const { comment, reviewers } = this.state;

    if (this.showCommentForm && !comment) {
      validationError.comment = constants.REQUIRED;
    }

    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;
    const { isAdmin, isTechWriter } = getUserRoles(userProfile);

    if (
      !isAdmin &&
      !isTechWriter &&
      this.canEditReviewers &&
      (!reviewers || reviewers.length <= 0)
    ) {
      validationError.reviewers = constants.PEER_REVIEWERS_MISSING;
    }
    this.setState({
      validationError,
    });

    return !this.hasError(validationError);
  };

  private hasError = (validationError: any) => {
    return (
      validationError && Object.keys(validationError).some((k) => validationError[k].length > 0)
    );
  };

  private handleCommentChange = (e: NonCancelableCustomEvent<TextareaProps.ChangeDetail>) => {
    const { value } = e.detail;

    this.setState(
      {
        comment: value,
      },
      () => this.validateInputs(),
    );
  };

  private updateReviewers = async (newReviewers: ContentAccessUser[]) => {
    const { onUpdateReviewers } = this.props;
    this.setState(
      {
        reviewers: newReviewers,
      },
      () => this.validateInputs(),
    );
    if (onUpdateReviewers) {
      onUpdateReviewers(newReviewers);
    }
  };

  private isNewOrModified = () => {
    const draftState = this.props.workflow?.draftState ?? '';
    return draftState === WorkflowState.NEW || draftState === WorkflowState.MODIFIED;
  };
}

export default WorkflowControls;
